import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, User, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { createPaymentPreference, redirectToCheckout } from '../services/mercadopago';
import copy from '../copy';

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
}

const CheckoutForm: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { openAuthModal } = useModal();
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-llenar información del usuario si está autenticado
  React.useEffect(() => {
    if (isAuthenticated && user) {
      setCustomerInfo({
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        phone: user.phone || '',
      });
    }
  }, [isAuthenticated, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatPrice = (price: number) => `${copy.comun_simbolo}${price} ${copy.comun_moneda}`;

  const validateForm = (): boolean => {
    if (!customerInfo.name.trim()) {
      setError('Por favor ingresa tu nombre completo');
      return false;
    }
    if (!customerInfo.email.trim()) {
      setError('Por favor ingresa tu correo electrónico');
      return false;
    }
    if (!customerInfo.email.includes('@')) {
      setError('Por favor ingresa un correo electrónico válido');
      return false;
    }
    if (!customerInfo.phone.trim()) {
      setError('Por favor ingresa tu número de teléfono');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (state.items.length === 0) {
      setError('Tu carrito está vacío');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Crear preferencia de pago en MercadoPago
      const preference = await createPaymentPreference(
        state.items,
        {
          name: customerInfo.name,
          email: customerInfo.email,
        }
      );

      // Redirigir al checkout de MercadoPago
      redirectToCheckout(preference);
    } catch (err) {
      console.error('Payment error:', err);
      setError('Error al procesar el pago. Por favor intenta de nuevo.');
      setIsProcessing(false);
    }
  };

  // Pre-llenar información del usuario si está autenticado
  React.useEffect(() => {
    if (!isAuthenticated) {
      openAuthModal('checkout');
    }
  }, [isAuthenticated, openAuthModal]);

  if (state.items.length === 0) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-4">{copy.carrito_vacio}</h1>
          <p className="text-primary-600 mb-8">{copy.carrito_vacio_desc}</p>
          <button
            onClick={() => navigate('/cursos')}
            className="btn-primary"
          >
            {copy.carrito_explorar}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 section-padding">
      <div className="container-max">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-primary-900 mb-4">
                {copy.checkout_titulo}
              </h1>
              <p className="text-lg text-primary-600">
                {copy.checkout_completar_desc}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Information */}
              <div className="card-minimal">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-accent-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-primary-900">
                    {copy.checkout_info_cliente}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-primary-700 mb-2">
                      {copy.checkout_nombre} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder={copy.checkout_nombre}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-primary-700 mb-2">
                      {copy.checkout_email} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder={copy.checkout_email}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-primary-700 mb-2">
                      {copy.checkout_telefono} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder={copy.checkout_telefono}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="card-minimal">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-primary-900">
                    {copy.checkout_metodo_pago}
                  </h2>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <h3 className="font-medium text-green-800">
                        {copy.checkout_pago_mercadopago}
                      </h3>
                      <p className="text-sm text-green-600">
                        {copy.checkout_pago_mercadopago_desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isProcessing}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>{copy.checkout_procesando}</span>
                  </>
                ) : (
                  <span>{copy.checkout_procesar_pago}</span>
                )}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <div className="card-minimal">
              <h2 className="text-xl font-semibold text-primary-900 mb-6">
                {copy.checkout_resumen}
              </h2>

              <div className="space-y-4">
                {state.items.map((item) => (
                  <div key={item.course.id} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <h3 className="font-medium text-primary-900">{item.course.title}</h3>
                      <p className="text-sm text-primary-600">
                        {copy.checkout_cantidad}: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-primary-900">
                        {formatPrice(item.course.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-primary-600">{copy.carrito_subtotal}</span>
                  <span className="font-medium text-primary-900">
                    {formatPrice(state.total)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-primary-600">{copy.carrito_impuestos}</span>
                  <span className="font-medium text-primary-900">
                    {formatPrice(0)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-lg font-semibold text-primary-900">{copy.carrito_total}</span>
                  <span className="text-lg font-bold text-primary-900">
                    {formatPrice(state.total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-blue-700">
                  {copy.carrito_pago_seguro}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm; 